<template>
  <div :class="classes">
    <DatePicker
      v-model="selectedDate"
      color="gray"
      :masks="masks"
      :attributes="attributes"
      :popover="popoverProps"
      :model-config="modelConfig"
      :disabled-dates="disabledDates"
      :min-date="minDate ? new Date(minDate) : undefined"
      :max-date="maxDate ? new Date(maxDate) : undefined"
      @input="onInput"
      @popoverDidShow="handleOpen"
      @popoverDidHide="handleClose"
    >
      <template v-slot="{ inputValue, inputEvents, showPopover }">
        <CalendarIcon
          :class="imageClasses"
          :data-cy="kebabCase(label + ' image')"
          @click="(e) => showCalendarFromIconClick && showPopover()"
          @keydown="(e) => showCalendarFromIconClick && showPopover()"
        />
        <input
          ref="input"
          autocomplete="off"
          type="text"
          :name="name"
          :value="inputValue"
          :data-cy="inputValue"
          :placeholder="placeholder"
          :label="label"
          :aria-label="label"
          :required="required"
          v-on="inputEvents"
          @blur="onBlur"
          @focus="onFocus"
        />
      </template>
    </DatePicker>
  </div>
</template>

<script>
import { DatePicker } from 'v-calendar';
import { format } from 'date-fns';
import kebabCase from '@/utils/kebabcase';

import CalendarIcon from '@/assets/icons/calendar.svg';

import 'v-calendar/dist/style.css';

export default {
  emits: [
    'focus',
    'input',
    'blur',
    'calendarOpened',
    'calendarClosed',
    'update:modelValue',
  ],
  components: { DatePicker, CalendarIcon },
  props: {
    name: { type: String, required: true },
    applyOldDesign: { type: Boolean, required: false, default: false },
    placeholder: String,
    label: { type: String, default: 'calendar' },
    required: Boolean,
    modelValue: [String, Date],
    highlightToday: Boolean,
    isActive: Boolean,
    minDate: [String, Date],
    maxDate: [String, Date],
    disabledDates: Object,
    showCalendarFromIconClick: Boolean,
  },
  data() {
    return {
      currentValue: null,
      modelConfig: {
        type: 'date',
        mask: 'YYYY-MM-DD',
      },
      selectedDate: null,
    };
  },
  mounted() {
    if (this.computedValue) {
      this.selectedDate = new Date(this.computedValue);
    }
  },
  computed: {
    attributes() {
      if (this.highlightToday) {
        return [
          {
            key: 'today',
            highlight: { color: 'gray', fillMode: 'light' },
            dates: new Date(),
          },
        ];
      }
      return [];
    },
    computedValue() {
      return this.inputValue ?? this.modelValue;
    },
    classes() {
      return {
        [this.$style.container]: true,
        [this.$style['design-input']]: this.applyOldDesign,
      };
    },
    imageClasses() {
      return {
        [this.$style.img]: true,
        [this.$style.active]: this.isActive,
        [this.$style.clickable]: this.showCalendarFromIconClick,
      };
    },
    masks() {
      return {
        input: 'YYYY-MM-DD',
      };
    },
    popoverProps() {
      const popoverProps = {};
      if (this.showCalendarFromIconClick) {
        popoverProps.visibility = 'focus';
      }
      return popoverProps;
    },
  },
  watch: {
    selectedDate(newVal) {
      this.$emit('update:modelValue', newVal);
    },
    modelValue(newVal) {
      if (newVal !== this.selectedDate) {
        this.selectedDate = newVal;
      }
    },
  },
  methods: {
    onInput(e) {
      try {
        this.currentValue = e ? format(new Date(e), 'yyyy-MM-dd') : '';
        this.$emit('input', { target: { value: this.currentValue } });
      } catch {} // Ignore invalid date formats
    },
    onFocus() {
      this.$emit('focus');
    },
    onBlur() {
      this.$emit('blur', {
        target: { value: this.computedValue, name: this.name },
      });
    },
    handleOpen() {
      this.$emit('calendarOpened');
    },
    handleClose() {
      this.$emit('calendarClosed', this.computedValue);
    },
    kebabCase,
  },
};
</script>
<style lang="less" module>
.container {
  width: 80%;
  &.design-input {
    width: 100%;
    .img {
      top: 12px;
      height: 20px;
    }
  }
  [class^='vc-pane-container'] {
    font-family: @regular-font;
  }
  [class^='vc-title'] {
    font-size: 16px;
    color: @cc-light-brown;
    font-weight: 400;
  }
  [class^='vc-weekday'] {
    font-size: 12px;
    font-weight: 400;
    color: @cc-light-brown;
  }
  [class^='vc-day-content'] {
    font-size: 16px;
    font-weight: 400 !important;
  }
  .img {
    position: absolute;
    right: 10px;
    top: 10px;
    width: 8%;
    height: 50%;
    &.active {
      path {
        stroke: @color-black-new;
      }
    }
  }
  .clickable {
    cursor: pointer;
  }
}
</style>
